/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ContentEditableEvent, IImageData, IRequestContext, Text } from '@msdyn365-commerce/core';
import * as React from 'react';
import { BackgroundImage } from './container-background-image/container-backgroud-image';

/**
 * Heading options
 */
export interface IHeadingComponentProps {
    /**
     *  Heading text
     */
    headingText?: string;
    /**
     * Heading tag (h1, h2, h3, h4, h5, h6)
     */
    tag?: HeadingTag;
    /**
     * Heading text size (sm, md, lg, xl)
     */
    textSize?: TextSize;

    requestContext: IRequestContext;

    handleTextChange(event: ContentEditableEvent): string;
}

export type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
export type TextSize = 'sm' | 'md' | 'lg' | 'xl';

/**
 * Container class options
 */
export interface IContainerProps {
    /**
     * CSS class name
     */
    className?: string;
    /**
     * Bootstrap supported container types
     */
    containerType?: string;
    /**
     * type name
     */
    typeName?: string;
    /**
     * id of container
     */
    id?: string;
    /**
     * React children
     */
    children: React.ReactNode;
    /**
     * Request context
     */
    request?: IRequestContext;
    /**
     * background image
     */
    backgroundImage?: IImageData;
    /**
     * background image repeat property
     */
    backgroundImageRepeat?: string;
    /**
     * background image size property
     */
    backgroundImageSize?: string;
    /**
     * module layout property
     */
    moduleLayout?: string;
    /**
     * background color
     */
    backgroundColor?: string;
}

const getContainerType = (containerType: string | undefined) => {
    switch (containerType) {
        case 'container':
            return 'container';
        case 'fluid':
        default:
            return 'fluid';
    }
};

/**
 * HOC to create bootstrap container div. If no container type is specified children are rendered.
 * @param options IContainerClasses
 */
export const Container: React.SFC<IContainerProps> = (props: IContainerProps): JSX.Element => {
    const {
        containerType,
        className,
        typeName,
        id,
        children,
        backgroundImage,
        request,
        backgroundImageRepeat,
        backgroundImageSize,
        moduleLayout,
        backgroundColor
    } = props;
    const customClassName = className ? ` ${className}` : '';

    let containerContent = children;
    if (containerType) {
        containerContent = <div className='row'>{containerContent}</div>;
    }

    if (backgroundImage && backgroundImage.src && backgroundImage.imageSettings && request) {
        containerContent = (
            <BackgroundImage
                containerType={typeName}
                request={request}
                image={backgroundImage}
                configs={{
                    repeat: backgroundImageRepeat,
                    size: backgroundImageSize,
                    layout: moduleLayout
                }}
            >
                {containerContent}
            </BackgroundImage>
        );
    }

    const containerStyle: React.CSSProperties | undefined = backgroundColor ? { backgroundColor } : undefined;

    return containerType ? (
        <div className={`${typeName} ${id} ${getContainerType(containerType)}${customClassName}`} style={containerStyle}>
            {containerContent}
        </div>
    ) : (
        <div className={`${typeName} ${id}${customClassName}`} style={containerStyle}>
            {containerContent}
        </div>
    );
};

export const HeadingComponent = (props: IHeadingComponentProps & { className: string }): JSX.Element | null => {
    const { tag, headingText, textSize, className } = props;
    const Tag = tag || 'h2';
    const classNameWithHeading = textSize ? `heading ${textSize} ${className}` : `heading ${className}`;
    return (
        (headingText && (
            <Text
                className={classNameWithHeading}
                tag={Tag}
                text={headingText}
                editProps={{ onEdit: props.handleTextChange, requestContext: props.requestContext }}
            />
        )) ||
        null
    );
};
